import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService } from '../services/authService'
import { User } from '../types/auth'
import toast from 'react-hot-toast'

// Bandera para logs de depuración (mantener en false para silenciar)
const DEBUG_LOGS = false

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  hasRole: (roleName: string) => boolean
  hasPermission: (permission: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user

  // Verificar token al iniciar
  useEffect(() => {
    const initAuth = async () => {
      if (DEBUG_LOGS) console.log('AuthContext: Initializing authentication...')
      const token = localStorage.getItem('accessToken')
      
      if (token) {
        if (DEBUG_LOGS) console.log('AuthContext: Token found, fetching user profile...')
        try {
          const userData = await authService.getProfile()
          if (DEBUG_LOGS) console.log('AuthContext: User data loaded:', userData)
          setUser(userData)
        } catch (error) {
          console.error('AuthContext: Error fetching profile, clearing tokens:', error)
          // Token inválido, limpiar localStorage
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
        }
      } else {
        if (DEBUG_LOGS) console.log('AuthContext: No token found in localStorage')
      }
      
      setLoading(false)
      if (DEBUG_LOGS) console.log('AuthContext: Initialization complete')
    }

    initAuth()
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password)
      
      localStorage.setItem('accessToken', response.accessToken)
      localStorage.setItem('refreshToken', response.refreshToken)
      
      setUser(response.user)
      toast.success('¡Bienvenido al panel administrativo!')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error al iniciar sesión'
      toast.error(message)
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    setUser(null)
    toast.success('Sesión cerrada exitosamente')
  }


  const hasRole = (roleName: string): boolean => {
    if (!user || !user.roles) return false
    return user.roles.some(role => role.name === roleName)
  }

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.roles) return false
    
    // Admin tiene todos los permisos
    if (hasRole('Admin')) return true
    
    // Verificar permisos en los roles del usuario
    return user.roles.some(role => {
      if (!role.permissions) return false
      
      let permissions
      try {
        // Si los permisos vienen como string JSON, parsearlos
        if (typeof role.permissions === 'string') {
          permissions = JSON.parse(role.permissions)
        } else {
          permissions = role.permissions
        }
      } catch (e) {
        permissions = {}
      }
      
      // Dividir el permiso en recurso y acción (ej: "users.read")
      const [resource, action] = permission.split('.')
      
      // Si tiene permiso "all" o el permiso específico
      return permissions?.all === true || permissions?.[resource]?.[action] === true
    })
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    hasRole,
    hasPermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
